import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  VStack,
  Tabs,
  Spinner,
  Center,
  Alert,
  Button,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiArrowLeft } from 'react-icons/fi'
import { useContactDetail, useUpdateContact, useAssignTag, useRemoveTag } from '../hooks/useContactDetail'
import { useAuth } from '../hooks/useAuth'
import {
  ContactProfileHeader,
  ContactInfoSection,
  DistrictsSection,
  ContactMethodArea,
  CanvassingHistorySection,
  NotesSection,
  SurveyResponsesSection,
  VoterRecordSection,
  ListMembershipsSection,
  TagsSection,
  EmploymentSection,
  ImpactSection,
} from '../components/contactDetail'

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: contact, isLoading, error } = useContactDetail(id)
  const updateMutation = useUpdateContact(id ?? '')
  const assignTagMutation = useAssignTag(id ?? '')
  const removeTagMutation = useRemoveTag(id ?? '')
  const { isOrganizer } = useAuth()

  const handleAssignTag = (tagId: string) => {
    assignTagMutation.mutate(tagId, {
      onSuccess: () => {
        toaster.create({ title: 'Tag assigned', type: 'success', duration: 2000 })
      },
      onError: () => {
        toaster.create({ title: 'Failed to assign tag', type: 'error', duration: 3000 })
      },
    })
  }

  const handleRemoveTag = (tagId: string) => {
    removeTagMutation.mutate(tagId, {
      onSuccess: () => {
        toaster.create({ title: 'Tag removed', type: 'success', duration: 2000 })
      },
      onError: () => {
        toaster.create({ title: 'Failed to remove tag', type: 'error', duration: 3000 })
      },
    })
  }

  const handleFieldSave = (fieldName: string, value: string) => {
    updateMutation.mutate(
      { [fieldName]: value },
      {
        onSuccess: () => {
          toaster.create({ title: 'Contact updated', type: 'success', duration: 2000 })
        },
        onError: () => {
          toaster.create({ title: 'Failed to update', type: 'error', duration: 3000 })
        },
      }
    )
  }

  if (isLoading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="accent" />
      </Center>
    )
  }

  if (error || !contact) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          Contact not found or an error occurred.
        </Alert.Root>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8} px={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch">
        <HStack>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><Icon asChild><FiArrowLeft /></Icon>Back
                      </Button>
        </HStack>

        <ContactProfileHeader contact={contact} />

        <ImpactSection
          flags={contact.analyticsFlags ?? []}
          impactScores={contact.impactScores ?? []}
        />

        {contact.catalistData?.isDeceased && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            This contact is marked as deceased in the voter file.
          </Alert.Root>
        )}

        <Tabs.Root variant='enclosed' colorPalette="brand" defaultValue="info">
          <Tabs.List overflowX="auto" overflowY="hidden"
            css={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              '& > button': { flexShrink: 0 }
            }}>
            <Tabs.Trigger value="info">Contact Info</Tabs.Trigger>
            <Tabs.Trigger value="tags">Tags ({contact.tags.length})</Tabs.Trigger>
            <Tabs.Trigger value="history">History ({contact.canvassingSessions.length})</Tabs.Trigger>
            <Tabs.Trigger value="notes">Notes ({contact.notes.length})</Tabs.Trigger>
            <Tabs.Trigger value="surveys">Surveys ({contact.surveyResponseGroups.length})</Tabs.Trigger>
            <Tabs.Trigger value="voterRecord">Voter Record</Tabs.Trigger>
            <Tabs.Trigger value="lists">Lists ({contact.listMemberships.length})</Tabs.Trigger>
          </Tabs.List>

          <Tabs.ContentGroup>
            <Tabs.Content value="info" px={0}>
              <VStack gap={6} align="stretch">
                <ContactInfoSection
                  contact={contact}
                  canEdit={isOrganizer}
                  onFieldSave={handleFieldSave}
                  isSaving={updateMutation.isPending}
                />
                <DistrictsSection contact={contact} canEdit={isOrganizer} />
                <ContactMethodArea contact={contact} canEdit={isOrganizer} />
                <EmploymentSection
                  contactId={contact.id}
                  employments={contact.employments}
                  canEdit={isOrganizer}
                />
              </VStack>
            </Tabs.Content>

            <Tabs.Content value="tags" px={0}>
              <TagsSection
                tags={contact.tags}
                canEdit={isOrganizer}
                onAssignTag={handleAssignTag}
                onRemoveTag={handleRemoveTag}
                isAssigning={assignTagMutation.isPending}
                isRemoving={removeTagMutation.isPending}
              />
            </Tabs.Content>

            <Tabs.Content value="history" px={0}>
              <CanvassingHistorySection sessions={contact.canvassingSessions} />
            </Tabs.Content>

            <Tabs.Content value="notes" px={0}>
              <NotesSection notes={contact.notes} />
            </Tabs.Content>

            <Tabs.Content value="surveys" px={0}>
              <SurveyResponsesSection groups={contact.surveyResponseGroups} />
            </Tabs.Content>

            <Tabs.Content value="voterRecord" px={0}>
              <VoterRecordSection voterRecord={contact.voterRecord} catalistData={contact.catalistData} />
            </Tabs.Content>

            <Tabs.Content value="lists" px={0}>
              <ListMembershipsSection memberships={contact.listMemberships} />
            </Tabs.Content>
          </Tabs.ContentGroup>
        </Tabs.Root>
      </VStack>
    </Container>
  );
}
